import classNames from 'classnames';
import unescape from 'lodash/unescape';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { DialogHeader, Textarea } from '@wg/wows-react-uikit';

import settings from '~/settings';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { showInviteAcceptDialog, showSendApplicationDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { isClanOnPreModeration } from '~/helpers/moderation';
import useScrollShadows from '~/hooks/useScrollShadows';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';
import { actionsClanEdit, changeClanAttributes } from '~/Actions/ActionClanEdit';

import ButtonJoinClan from '~/UIKit/ButtonJoinClan/ButtonJoinClan';
import SocialLinkContainer from '~/UIKit/SocialLink/SocialLinkContainer';

import styles from './ClanDescription.scss';
import ClanDescriptionFooter from './ClanDescriptionFooter';
import ClanDescriptionPreModeraion from './ClanDescriptionPreModeraion';

import {
  addProtocol,
  getNormalizedDescription,
  parseDescriptionEditError,
  prepareClanSocials,
} from './clanDescription.utils';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanInvitationMinimal } from '~/Actions/ActionInvites';
import type { AppStateType as RootState } from '~/Reducers';
import type { SOCIAL_NETWORKS } from '~/constants';
import type { ClanPreloaded } from '~/types/declaration';

const TEXTAREA_MIN_HEIGHT = 41;

type IFocusElMark = undefined | 'description' | SOCIAL_NETWORKS;

const stateSelector = (state: RootState) => {
  return {
    editDescriptionState: state.clanEdit?.clanDescription,
  };
};

interface ClanDescriptionProps {
  clan: ClanInfoType | ClanPreloaded;
  isEditMode?: boolean;
  source?: ENTRY_SOURCE;
  hideDialog: () => void;
}

const ClanDescription = ({ clan, isEditMode = false, source, hideDialog }: ClanDescriptionProps) => {
  const dispatch = useAppDispatch();

  const { editDescriptionState } = useAppSelector(stateSelector, shallowEqual);
  const currentAccount = useAppSelector(currentAccountSelector);

  const containerRef = useRef<HTMLDivElement>(null);
  useScrollShadows(containerRef);

  const isOwnClan = clan.id === currentAccount.clanId;
  const isEditButtonShown = isOwnClan && hasPermission(currentAccount.roleName, PERMISSIONS.CHANGE_SETTINGS);
  const isEditingAvailable = isEditButtonShown && settings.editClanDescription.allowed;

  const [isEditing, setIsEditing] = useState<boolean>(isEditMode);
  const [focusElMark, setFocusElMark] = useState<IFocusElMark>();

  // Description
  const { error, isValidationError } = parseDescriptionEditError(editDescriptionState);
  const rawDescription = getNormalizedDescription(unescape(clan?.rawDescription || ''));

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [description, setDescription] = useState<string>(rawDescription);
  const [descriptionHeight, setDescriptionHeight] = useState<number>(TEXTAREA_MIN_HEIGHT);

  useEffect(() => {
    if (descriptionRef.current?.offsetHeight !== descriptionHeight) {
      setDescriptionHeight(Math.max(descriptionRef.current?.offsetHeight || 0, TEXTAREA_MIN_HEIGHT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, isEditing]);

  useEffect(() => {
    setDescription(rawDescription);
  }, [rawDescription]);

  const onDescriptionChange = useCallback(
    (newDescription: string) => {
      const safeDescription = unescape(newDescription);

      setDescription(safeDescription);
      dispatch(
        actionsClanEdit.updateClanEditState('clanDescription', {
          savedDescription: safeDescription,
          isValidationError,
          error,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDescription],
  );

  // Social networks
  const clanSocials = useMemo(() => clan?.communityUrls || {}, [clan?.communityUrls]);

  const [socials, setSocials] = useState(prepareClanSocials(clan?.communityUrls));

  useEffect(() => {
    setSocials(prepareClanSocials(clan?.communityUrls));
  }, [clan?.communityUrls]);

  const { isSocialError, isSocialChanges } = useMemo(() => {
    return Object.keys(socials).reduce(
      ({ isSocialError, isSocialChanges }, social) => {
        const socialObj = socials[social as SOCIAL_NETWORKS];
        return {
          isSocialError: isSocialError || Boolean(socialObj && socialObj.error) || false,
          isSocialChanges: isSocialChanges || clanSocials[social] !== (socialObj && socialObj.link) || false,
        };
      },
      {
        isSocialError: false,
        isSocialChanges: false,
      },
    );
  }, [socials, clanSocials]);

  const onSocialChange = useCallback(
    ({ social, link, error }: { social: SOCIAL_NETWORKS; link: string; error: boolean }) => {
      setSocials((socials) => {
        return {
          ...socials,
          [social]: {
            link,
            error,
          },
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDescription, setSocials, socials],
  );

  const renderClanSocialNetworks = () => {
    if (!settings.communityServices?.length || (!Object.values(socials).length && !isEditingAvailable)) {
      return null;
    }

    return (
      <div className={classNames(styles.socials)}>
        <h4 className={classNames(styles.header, styles.small)}>{t("Clan's Discord server")}</h4>
        <div>
          {settings.communityServices?.map((social) => {
            return (
              <SocialLinkContainer
                link={socials[social]?.link || ''}
                social={social}
                isDisabled={editDescriptionState?.isFetching || isOnPreModeration}
                isEditAvailable={isEditingAvailable && !isOnPreModeration}
                isEditMode={isEditing}
                onEditMode={() => onFormEdit(social)}
                isFocusOn={focusElMark === social}
                isFullLink={isOwnClan && isEditingAvailable}
                onChange={onSocialChange}
                key={`clan_social_${social}`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Form
  const onFormEdit = useCallback(
    (elMark: IFocusElMark) => {
      if (elMark === 'description') {
        dwhExport.push(DWH_EVENTS.DESCRIPTION_MODAL.EDIT_CLICK);
      } else if (elMark) {
        dwhExport.push(DWH_EVENTS.DESCRIPTION_MODAL.ADD_SOCIAL_CLICK, {
          social: elMark,
        });
      }
      if (editDescriptionState?.savedDescription) {
        setDescription(editDescriptionState?.savedDescription);
      }

      setFocusElMark(elMark);

      setDescriptionHeight(Math.max(descriptionRef.current?.offsetHeight || 0, TEXTAREA_MIN_HEIGHT));
      setIsEditing(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [descriptionRef.current, setDescriptionHeight, setIsEditing],
  );

  const onFormCancel = useCallback(() => {
    if (!isEditing) {
      return;
    }
    dwhExport.push(DWH_EVENTS.DESCRIPTION_MODAL.CANCEL_CLICK);

    setDescription(rawDescription);
    setSocials(prepareClanSocials(clan?.communityUrls));
    setIsEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clan?.communityUrls, isEditing]);

  const onFormSubmit = useCallback(() => {
    dwhExport.push(DWH_EVENTS.DESCRIPTION_MODAL.SAVE_CLICK);

    const newDescription = getNormalizedDescription(description);
    const newSocials = (Object.keys(socials) as Array<keyof typeof socials>).reduce(
      (acc, socialName) => {
        const socialObj = socials[socialName];
        if (socialObj?.link) {
          return {
            ...acc,
            [socialName]: addProtocol(socialObj.link),
          };
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    void dispatch(
      changeClanAttributes({
        description: newDescription,
        communityUrls: newSocials,
      }),
    ).then((isSuccess) => {
      if (isSuccess) {
        setDescription(newDescription);
        setIsEditing(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socials, description, setDescription, setDescriptionHeight, setIsEditing]);

  useEffect(() => {
    if (!hasPermission(currentAccount.roleName, PERMISSIONS.CHANGE_SETTINGS)) {
      onFormCancel();
    }
  }, [currentAccount, hideDialog, onFormCancel]);

  // Premoderation
  const isOnPreModeration = isClanOnPreModeration(clan);

  // Button states
  const isSaveBtnDisabled =
    isSocialError ||
    (!isSocialChanges && description === rawDescription) ||
    !!error ||
    editDescriptionState?.isFetching ||
    isOnPreModeration;
  let saveBtnTooltip = error && isValidationError ? t('Введите корректное описание') : null;
  if (description === rawDescription) {
    saveBtnTooltip = description ? t('Измените описание') : t('Введите описание, удовлетворяющее Правилам кланов.');
  }

  // Join button
  const onAcceptInvite = useCallback(
    (invite: ClanInvitationMinimal) => {
      hideDialog();
      dispatch(showInviteAcceptDialog(invite));
    },
    [dispatch, hideDialog],
  );

  const onSendApplication = useCallback(
    (clan: ClanInfoType) => {
      dispatch(showSendApplicationDialog(clan, source || ENTRY_SOURCE.CLAN_PROFILE));
    },
    [dispatch, source],
  );

  const joinClanButton = useMemo(() => {
    if (!currentAccount.id || currentAccount.clanId) {
      return null;
    }

    return (
      <ButtonJoinClan
        clan={clan}
        currentAccount={currentAccount}
        onAcceptInvite={onAcceptInvite}
        onSendApplication={onSendApplication}
        size={'isDefault'}
        onClick={() => {
          dwhExport.push(DWH_EVENTS.DESCRIPTION_MODAL.JOIN_CLICK, {
            clan_id: clan.id,
          });
        }}
      />
    );
  }, [clan, currentAccount, onAcceptInvite, onSendApplication]);

  return (
    <>
      <div ref={containerRef} className={styles.container}>
        <DialogHeader>{t('Description')}</DialogHeader>
        <div className={styles.descriptionWrapper}>
          <div
            ref={descriptionRef}
            className={classNames(styles.description, {
              [styles.isEditingHelper]: isEditing,
            })}
          >
            {description ||
              (!isEditing && <span className={styles.descriptionPlaceholder}>{t('No description')}</span>)}
          </div>
          {isEditing && (
            <Textarea
              className={classNames(styles.description, styles.isEditing)}
              value={description}
              width="100%"
              height={`${descriptionHeight}px`}
              maxLength={500}
              error={error ?? undefined}
              isDisabled={editDescriptionState?.isFetching || isOnPreModeration}
              isCounted
              isFocusOn={focusElMark === 'description'}
              onChange={onDescriptionChange}
            />
          )}
        </div>
        {renderClanSocialNetworks()}
        {isEditingAvailable && isOnPreModeration && <ClanDescriptionPreModeraion clan={clan} />}
      </div>
      <ClanDescriptionFooter
        isEditingAvailable={isEditingAvailable}
        isEditMode={isEditMode}
        isEditing={isEditing}
        isEditBtnDisabled={isOnPreModeration}
        isSaveBtnDisabled={isSaveBtnDisabled}
        saveBtnTooltip={saveBtnTooltip}
        onHideDialog={hideDialog}
        joinClanButton={joinClanButton}
        onFormEdit={() => onFormEdit('description')}
        onFormSubmit={onFormSubmit}
        onFormCancel={onFormCancel}
      />
    </>
  );
};

export default memo(ClanDescription);
